/**
 * Validation service for Certificate Search forms
 */

import type { FormData, ValidationError } from '../types';
import { DomUtils } from '../utils/DomUtils';

export class ValidationService {
    /**
     * Validate certificate number
     */
    static validateCertificateNumber(certificateNumber: string, translations: any = {}): ValidationError[] {
        const errors: ValidationError[] = [];
        const trimmed = DomUtils.trim(certificateNumber);

        if (trimmed.length === 0) {
            errors.push({
                field: 'certificatenr',
                message: translations['error.certificate_number.required'] || 'Certificate number is required',
                key: 'error.certificate_number.required',
            });
        } else if (trimmed.length < 5) {
            errors.push({
                field: 'certificatenr',
                message: translations['error.certificate_number.min_length'] || 'Certificate number must contain at least 5 characters',
                key: 'error.certificate_number.min_length',
            });
        }

        return errors;
    }

    /**
     * Validate advanced search fields
     */
    static validateAdvancedSearch(formData: FormData, translations: any = {}): ValidationError[] {
        const errors: ValidationError[] = [];

        const company = DomUtils.trim(formData.company);
        const norm = DomUtils.trim(formData.norm);
        const country = DomUtils.trim(formData.country);

        // Company is required
        if (company.length === 0) {
            errors.push({
                field: 'company',
                message: translations['error.company.required'] || 'Company field is required',
                key: 'error.company.required',
            });
        }

        // Norm is required
        if (norm.length === 0) {
            errors.push({
                field: 'norm',
                message: translations['error.norm.required'] || 'Norm field is required',
                key: 'error.norm.required',
            });
        }

        // Country must be exactly 2 characters or empty
        if (country.length !== 0 && country.length !== 2) {
            errors.push({
                field: 'country',
                message: translations['error.country.format'] || 'Country code must be exactly 2 characters',
                key: 'error.country.format',
            });
        }

        return errors;
    }

    /**
     * Validate all form data based on search mode
     */
    static validateForm(formData: FormData, searchMode: 'certificate' | 'advanced', translations: any = {}): ValidationError[] {
        if (searchMode === 'certificate') {
            const errors = this.validateCertificateNumber(formData.certificatenr, translations);

            // Cobra-Regel: Cert-Nr + (Name ODER Geburtsdatum) — beides leer ist nicht erlaubt
            const name = DomUtils.trim(formData.name);
            const birthdate = DomUtils.trim(formData.birthdate);
            if (name.length === 0 && birthdate.length === 0) {
                const message = translations['error.name_or_birthdate.required'] || 'Please enter at least the last name or the birthdate.';
                errors.push({ field: 'name', message, key: 'error.name_or_birthdate.required' });
                errors.push({ field: 'birthdate', message, key: 'error.name_or_birthdate.required' });
            }

            return errors;
        } else {
            return this.validateAdvancedSearch(formData, translations);
        }
    }

    /**
     * Get error messages for specific field
     */
    static getFieldErrors(errors: ValidationError[], field: string): ValidationError[] {
        return errors.filter((error) => error.field === field);
    }

    /**
     * Check if validation has errors
     */
    static hasErrors(errors: ValidationError[]): boolean {
        return errors.length > 0;
    }

    /**
     * Group errors by field
     */
    static groupErrorsByField(errors: ValidationError[]): Record<string, ValidationError[]> {
        return errors.reduce(
            (grouped, error) => {
                const field = error.field;
                if (!grouped[field]) {
                    grouped[field] = [];
                }
                grouped[field].push(error);
                return grouped;
            },
            {} as Record<string, ValidationError[]>
        );
    }
}
